import React from "react";
import { ArrowRight } from "lucide-react";
import { OUTCOMES, EES_INTRO, DIFFERENTIATORS } from "@/data/content";
import { Container, ChapterHeader, Section, Reveal, FlyIn } from "@/components/landing/shared";
import { CheckGlyph } from "@/components/landing/icons";

const renderDeliverable = (text) => {
  const parts = text.split(" – ");
  if (parts.length !== 2) return text;
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{parts[0]}</span>
      <ArrowRight className="h-3 w-3 shrink-0 text-[color:var(--muted-ink)]" aria-hidden="true" />
      <span>{parts[1]}</span>
    </span>
  );
};

export const Outcomes = () => {
  return (
    <Section id="outcomes" navId="outcomes" className="bg-[color:var(--ivory)] hairline-top overflow-hidden">
      <Container className="section-y">
        <Reveal className="mx-auto mb-10 max-w-3xl text-center sm:mb-12" data-testid="ees-intro">
          <p className="text-pretty text-[1rem] leading-[1.7] text-[color:var(--muted-ink)]">{EES_INTRO}</p>
        </Reveal>

        <div className="mx-auto mb-10 max-w-3xl sm:mb-12" data-testid="differentiators-block">
          <ChapterHeader
            stacked
            className="mb-6 sm:mb-8"
            eyebrow="Why HR Anexi"
            title={DIFFERENTIATORS.heading}
            testId="differentiators-section-header"
          />

          <ul className="grid gap-x-8 gap-y-3.5 sm:grid-cols-2" data-testid="differentiators-list">
            {DIFFERENTIATORS.items.map((item, i) => (
              <Reveal
                as="li"
                key={item}
                delay={i}
                className="flex items-start gap-3.5"
                data-testid={`differentiator-item-${i + 1}`}
              >
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--navy)] text-[color:var(--gold)]">
                  <CheckGlyph className="h-3.5 w-3.5" />
                </span>
                <span className="text-[0.98rem] leading-[1.5] text-[color:var(--ink)]">{item}</span>
              </Reveal>
            ))}
          </ul>
        </div>

        <div className="mx-auto max-w-3xl">
          <ChapterHeader
            index={5}
            total={9}
            stacked
            className="mb-6 sm:mb-8"
            eyebrow="What management gets"
            title={OUTCOMES.heading}
            testId="outcomes-section-header"
          />

          <ul className="divide-y divide-[color:var(--hairline)] border-y border-[color:var(--hairline)]" data-testid="outcomes-list">
            {OUTCOMES.items.map((item, i) => (
              <Reveal as="li" key={item} delay={i} className="flex items-start gap-4 py-3.5" data-testid={`outcome-item-${i + 1}`}>
                <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[color:var(--navy)] text-[color:var(--gold)]">
                  <CheckGlyph className="h-3.5 w-3.5" />
                </span>
                <span className="text-[0.98rem] leading-[1.5] text-[color:var(--ink)]">{item}</span>
              </Reveal>
            ))}
          </ul>

          <Reveal delay={5} className="mt-5 flex flex-wrap items-center gap-3">
            {OUTCOMES.deliverables.map((d) => (
              <span key={d} className="rounded-full border border-[color:var(--hairline)] bg-white px-3.5 py-1.5 text-[0.85rem] font-medium text-[color:var(--ink)]" data-testid="outcomes-deliverable-pill">{renderDeliverable(d)}</span>
            ))}
          </Reveal>

          <FlyIn from="left" distance="80vw" settleAt={0.6} className="mt-8 rounded-3xl border border-[color:var(--hairline)] bg-white p-6 shadow-[0_18px_50px_rgba(14,23,32,0.06)] sm:p-8" data-testid="survey-outcomes-block">
            {OUTCOMES.cultureParagraphs.map((p, i) => (
              <p key={p.slice(0, 40)} className={`text-pretty text-[0.95rem] leading-[1.65] text-[color:var(--muted-ink)] ${i > 0 ? "mt-4" : ""}`} data-testid={`culture-paragraph-${i + 1}`}>
                {p}
              </p>
            ))}
          </FlyIn>
        </div>
      </Container>
    </Section>
  );
};
